import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Package, X } from "lucide-mui";
import { useState, type ChangeEvent } from "react";
import {
  api,
  getErrorMessage,
  type MicroApp,
  type MicroAppAccessMode,
  type MicroAppPublishBody,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../../auth/scopes";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import { formatBytes } from "../../../utils/format.ts";

// Mirrors svc-backend micro_apps/validation.py. The backend is the authority;
// these only let the uploader see an oversized file before sending it.
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;
const KB = 1024;

type SlotKey = "manifest" | "client" | "server";

interface SlotSpec {
  label: string;
  accept: string;
  limit: number;
}

const SLOTS: Record<SlotKey, SlotSpec> = {
  manifest: { label: "Manifest", accept: ".json,application/json", limit: 32 * KB },
  client: { label: "Client code", accept: ".html,.htm,.js,text/html", limit: 512 * KB },
  server: { label: "Server code", accept: ".js,.mjs,text/javascript", limit: 128 * KB },
};

/** What a slot will send: the content plus where it came from, for display. */
interface SlotValue {
  value: string | null;
  source: string;
}

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/**
 * Upload a micro app's files directly, for apps too large to publish over MCP
 * in a single tool call. `app === null` creates a new app with its first
 * version (atomically); otherwise it appends a version to `app`, carrying over
 * any file not replaced from the live version.
 *
 * Accepts the three files separately, or one JSON bundle
 * `{manifest, client_code, server_code, notes, slug, title, description}` —
 * the shape the `publish_micro_app` tool tells an agent to write to disk.
 */
function MicroAppUploadDialog({
  app,
  onClose,
  onDone,
}: {
  app: MicroApp | null;
  onClose: () => void;
  onDone: (warnings: string[]) => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const canPublishGlobal = userHasGlobalScope(user, "dev:read");
  const creating = app === null;

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // A new app lands in the tenant selected in the sidebar switcher; developers
  // may publish it globally instead.
  const [publishGlobal, setPublishGlobal] = useState(false);
  const [accessMode, setAccessMode] = useState<MicroAppAccessMode>("restricted");
  const [notes, setNotes] = useState("");
  const [publish, setPublish] = useState(true);
  // A slot the user hasn't touched falls back to the live version's content.
  const [overrides, setOverrides] = useState<Partial<Record<SlotKey, SlotValue>>>({});
  const [error, setError] = useState<string | null>(null);

  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: keys.microApp.versions(app?.id),
    queryFn: () => api.getMicroAppVersions(app!.id),
    enabled: !creating,
  });
  const live = versions?.find((v) => v.id === app?.current_version_id) ?? null;

  const liveSlot = (key: SlotKey): SlotValue => {
    if (!live) return { value: null, source: "none" };
    const value =
      key === "manifest"
        ? JSON.stringify(live.manifest, null, 2)
        : key === "client"
          ? live.client_code
          : live.server_code;
    return value == null ? { value: null, source: "none" } : { value, source: `live v${live.version}` };
  };
  const slot = (key: SlotKey): SlotValue => overrides[key] ?? liveSlot(key);

  const setSlot = (key: SlotKey, value: SlotValue) =>
    setOverrides((cur) => ({ ...cur, [key]: value }));

  const readFile = async (e: ChangeEvent<HTMLInputElement>): Promise<File | null> => {
    const file = e.target.files?.[0] ?? null;
    // Reset so picking the same file again (after editing it) still fires.
    e.target.value = "";
    return file;
  };

  const onSlotFile = async (key: SlotKey, e: ChangeEvent<HTMLInputElement>) => {
    const file = await readFile(e);
    if (!file) return;
    setSlot(key, { value: await file.text(), source: file.name });
    setError(null);
  };

  const onBundleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = await readFile(e);
    if (!file) return;
    let bundle: Record<string, unknown>;
    try {
      bundle = JSON.parse(await file.text());
      if (typeof bundle !== "object" || bundle === null || Array.isArray(bundle)) {
        throw new Error("expected a JSON object");
      }
    } catch (err) {
      setError(`${file.name} is not a valid bundle: ${getErrorMessage(err)}`);
      return;
    }
    if (bundle.manifest == null) {
      setError(`${file.name} has no "manifest" — a bundle needs at least that`);
      return;
    }
    const source = `${file.name} (bundle)`;
    const asText = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);
    setOverrides({
      manifest: {
        value:
          typeof bundle.manifest === "string"
            ? bundle.manifest
            : JSON.stringify(bundle.manifest, null, 2),
        source,
      },
      // A bundle is the whole app: a field it leaves out means "no such code",
      // not "keep the live one".
      client: { value: asText(bundle.client_code), source: bundle.client_code ? source : "none" },
      server: { value: asText(bundle.server_code), source: bundle.server_code ? source : "none" },
    });
    if (typeof bundle.notes === "string") setNotes(bundle.notes);
    if (creating) {
      if (typeof bundle.slug === "string") setSlug(bundle.slug);
      if (typeof bundle.title === "string") setTitle(bundle.title);
      if (typeof bundle.description === "string") setDescription(bundle.description);
    }
    setError(null);
  };

  const submit = useMutation({
    mutationFn: async (): Promise<string[]> => {
      const manifestText = slot("manifest").value;
      if (!manifestText) throw new Error("A manifest is required");
      let manifest: Record<string, unknown>;
      try {
        manifest = JSON.parse(manifestText);
      } catch (err) {
        throw new Error(`Manifest is not valid JSON: ${getErrorMessage(err)}`);
      }
      const body: MicroAppPublishBody = {
        manifest,
        client_code: slot("client").value,
        server_code: slot("server").value,
        notes: notes.trim() || null,
        publish,
      };
      if (app === null) {
        if (!SLUG_RE.test(slug)) {
          throw new Error(
            "Slug must be 2-64 lowercase letters, digits or hyphens, starting with a letter or digit",
          );
        }
        if (!title.trim()) throw new Error("Title is required");
        if (!publishGlobal && !selectedTenant) {
          throw new Error("Select a tenant in the sidebar first");
        }
        const res = await api.createMicroApp({
          tenant_id: publishGlobal ? null : selectedTenant!.id,
          slug,
          title: title.trim(),
          description,
          access_mode: accessMode,
          initial_version: body,
        });
        return res.warnings;
      }
      const res = await api.publishMicroAppVersion(app.slug, body);
      return res.warnings;
    },
    onSuccess: (warnings) => {
      queryClient.invalidateQueries({ queryKey: keys.microApp.all });
      onDone(warnings);
    },
    onError: (e) => setError(getErrorMessage(e)),
    meta: { suppressErrorToast: true },
  });

  const oversized = (Object.keys(SLOTS) as SlotKey[]).some((key) => {
    const v = slot(key).value;
    return v != null && byteLength(v) > SLOTS[key].limit;
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{app === null ? "New micro app" : `Upload version: ${app.title}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {creating ? (
            <>
              <TextField
                label="Slug"
                size="small"
                value={slug}
                onChange={(e) => setSlug(e.target.value.trim().toLowerCase())}
                helperText="The app's address: /apps/<slug>"
                required
              />
              <TextField
                label="Title"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <TextField
                label="Description"
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Tenant"
                  size="small"
                  value={
                    publishGlobal
                      ? "Global (all tenants)"
                      : (selectedTenant?.name ?? "No tenant selected")
                  }
                  disabled
                  error={!publishGlobal && !selectedTenant}
                  helperText={publishGlobal ? undefined : "Change with the sidebar tenant switcher"}
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  label="Access"
                  size="small"
                  value={accessMode}
                  onChange={(e) => setAccessMode(e.target.value as MicroAppAccessMode)}
                  sx={{ flex: 1 }}
                  helperText={
                    accessMode === "restricted" ? "Grant a group access afterwards" : undefined
                  }
                >
                  <MenuItem value="restricted">Restricted</MenuItem>
                  <MenuItem value="everyone">Everyone in tenant</MenuItem>
                </TextField>
              </Stack>
              {canPublishGlobal ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={publishGlobal}
                      onChange={(e) => setPublishGlobal(e.target.checked)}
                    />
                  }
                  label="Publish globally (all tenants)"
                />
              ) : null}
              <Divider />
            </>
          ) : null}

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<Package fontSize="small" />}
            >
              Load bundle (.json)
              <input hidden type="file" accept=".json,application/json" onChange={onBundleFile} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              or pick the files one by one below
            </Typography>
          </Stack>

          {versionsLoading ? (
            <CircularProgress size={20} />
          ) : (
            (Object.keys(SLOTS) as SlotKey[]).map((key) => {
              const spec = SLOTS[key];
              const current = slot(key);
              const size = current.value != null ? byteLength(current.value) : 0;
              const tooBig = size > spec.limit;
              return (
                <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">{spec.label}</Typography>
                      <Typography
                        variant="caption"
                        color={tooBig ? "error" : "text.secondary"}
                        noWrap
                        component="div"
                      >
                        {current.value == null
                          ? key === "manifest"
                            ? "none — required"
                            : "none"
                          : `${current.source} · ${formatBytes(size)} of ${formatBytes(spec.limit)}`}
                      </Typography>
                    </Box>
                    <Button
                      component="label"
                      size="small"
                      startIcon={<FileUp fontSize="small" />}
                    >
                      Choose file
                      <input
                        hidden
                        type="file"
                        accept={spec.accept}
                        onChange={(e) => onSlotFile(key, e)}
                      />
                    </Button>
                    {current.value != null && key !== "manifest" ? (
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<X fontSize="small" />}
                        onClick={() => setSlot(key, { value: null, source: "none" })}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })
          )}

          <TextField
            label="Notes"
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What changed in this version"
          />
          <FormControlLabel
            control={<Switch checked={publish} onChange={(e) => setPublish(e.target.checked)} />}
            label="Make live immediately"
          />

          {error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => submit.mutate()}
          loading={submit.isPending}
          disabled={
            versionsLoading ||
            oversized ||
            !slot("manifest").value ||
            (creating && !publishGlobal && !selectedTenant)
          }
        >
          {creating ? "Create app" : "Upload version"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MicroAppUploadDialog;
